import React from 'react';

const TechSection: React.FC = () => {
  const techStack = [
    {
      layer: 'AI 大脑',
      icon: '🧠',
      color: 'border-orange-500/30 bg-orange-500/5',
      items: [
        { name: '多模型适配', desc: '灵活支持 Gemini、通义千问等，可按需切换' },
        { name: '多模态感知', desc: '语音 + 摄像头 + 屏幕共享，全方位交互' },
        { name: 'AI 性格配置', desc: '自定义 AI 人设、知识领域与交互风格' },
      ]
    },
    {
      layer: '通信层',
      icon: '📡',
      color: 'border-blue-500/30 bg-blue-500/5',
      items: [
        { name: 'MQTT 协议', desc: '轻量级物联网通信，发布/订阅模式' },
        { name: 'WebSocket', desc: '浏览器与 MQTT Broker 的桥接' },
        { name: '浏览器交互', desc: '打开本网站即可与台灯对话，无需安装' },
      ]
    },
    {
      layer: '硬件层',
      icon: '⚡',
      color: 'border-emerald-500/30 bg-emerald-500/5',
      items: [
        { name: '舵机驱动', desc: 'PRO 采用串口总线舵机（丝滑+反馈），Lite 采用 MG996R（高性价比）' },
        { name: '多种上位机', desc: '支持 DFRobot K10、M5Stack CoreS3、树莓派等' },
        { name: 'WS2812B LED', desc: '可编程 RGB LED，支持亮度与色彩调节' },
      ]
    },
    {
      layer: '结构层',
      icon: '🔧',
      color: 'border-pink-500/30 bg-pink-500/5',
      items: [
        { name: '3D 打印', desc: 'FDM 工艺，PLA/PETG 材质' },
        { name: '参数化设计', desc: '开源 CAD 文件，支持自定义修改' },
        { name: '模块化组装', desc: '螺丝连接，易于拆装和维护' },
      ]
    },
  ];

  return (
    <section id="tech" className="py-24 relative">
      <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950" />
      
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="text-center mb-16">
          <span className="text-blue-400 text-sm font-semibold uppercase tracking-wider">技术架构</span>
          <h2 className="text-4xl lg:text-5xl font-bold text-white mt-3 mb-4">四层技术栈</h2>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">从云端 AI 到本地硬件，每一层都经过精心设计</p>
        </div>

        {/* Architecture Diagram */}
        <div className="max-w-4xl mx-auto space-y-4">
          {techStack.map((layer, i) => (
            <div key={i} className={`rounded-2xl border ${layer.color} p-6 card-hover`}>
              <div className="flex items-center gap-3 mb-4">
                <span className="text-2xl">{layer.icon}</span>
                <h3 className="text-xl font-bold text-white">{layer.layer}</h3>
                <div className="flex-grow h-px bg-slate-800 ml-4" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {layer.items.map((item, j) => (
                  <div key={j} className="bg-slate-900/50 rounded-xl p-4 border border-slate-800/50">
                    <h4 className="text-white font-semibold text-sm mb-1">{item.name}</h4>
                    <p className="text-slate-500 text-xs">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Data Flow */}
        <div className="mt-16 text-center">
          <h3 className="text-xl font-bold text-white mb-6">数据流</h3>
          <div className="flex flex-wrap items-center justify-center gap-3 text-sm">
            {[
              { label: '语音/摄像头/屏幕', color: 'bg-orange-500/20 text-orange-300 border-orange-500/30' },
              { label: '→', color: 'text-slate-600' },
              { label: 'AI 大模型', color: 'bg-purple-500/20 text-purple-300 border-purple-500/30' },
              { label: '→', color: 'text-slate-600' },
              { label: 'Function Call', color: 'bg-blue-500/20 text-blue-300 border-blue-500/30' },
              { label: '→', color: 'text-slate-600' },
              { label: 'MQTT 消息', color: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' },
              { label: '→', color: 'text-slate-600' },
              { label: 'ESP32 执行', color: 'bg-pink-500/20 text-pink-300 border-pink-500/30' },
            ].map((item, i) => (
              item.label === '→' 
                ? <span key={i} className={item.color}>→</span>
                : <span key={i} className={`px-3 py-1.5 rounded-lg border ${item.color} font-medium`}>{item.label}</span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TechSection;
